import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Popover } from "antd";
import { getNodeEntry } from "components/CC/Nodes/node-config";
import { NodeType } from "components/CC/types/enums";
import { PARAMETER_COLORS } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { type TagParamType } from "components/CC/UI/TagInput/tagInputTypes";
import { inferParamType } from "components/CC/UI/TagInput/tagInputUtils";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import {
    $applyNodeReplacement,
    $createTextNode,
    $getNodeByKey,
    DecoratorNode,
    type LexicalNode,
    type NodeKey,
    type SerializedLexicalNode,
    type Spread,
} from "lexical";
import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "react-toastify";

// ============================================================================
// Types
// ============================================================================

interface SerializedTagNode
    extends Spread<
        {
            inner: string;
            paramType: TagParamType;
            valid?: boolean;
            displayLabel?: string;
            nodeType?: string;
        },
        SerializedLexicalNode
    > {
    type: "tag";
    version: 1;
}

// ============================================================================
// Helpers
// ============================================================================

const getPillColor = (paramType: TagParamType): string => {
    if (paramType === "form_fields") return PARAMETER_COLORS.form_fields;
    if (paramType === "global_fields") return PARAMETER_COLORS.global_fields;
    if (paramType === "local_fields") return PARAMETER_COLORS.local_fields;
    if (paramType === "rows") return PARAMETER_COLORS.rows;
    if (paramType === "system") return PARAMETER_COLORS.system;
    if (paramType === "steps") return PARAMETER_COLORS.steps;
    if (paramType === "http_steps") return PARAMETER_COLORS.http_steps;
    if (paramType === "formulas") return PARAMETER_COLORS.formulas;
    if (paramType === "format_convert") return PARAMETER_COLORS.format_convert;
    if (paramType === "math_functions") return PARAMETER_COLORS.math_functions;
    if (paramType === "operators") return PARAMETER_COLORS.operators;
    return PARAMETER_COLORS.keys;
};
const INVALID_PILL_COLOR = "#000000";

interface TagNodeViewProps {
    color: string;
    inner: string;
    displayLabel: string;
    nodeKey: string;
    paramType: TagParamType;
    nodeType?: string;
}

const TagNodeView = ({ color, inner, displayLabel, nodeKey, paramType, nodeType }: TagNodeViewProps): ReactNode => {
    const NodeIcon = nodeType ? getNodeEntry(nodeType as NodeType)?.icon : undefined;
    const [editor] = useLexicalComposerContext();
    const [open, setOpen] = useState(false);
    const firstActionRef = useRef<HTMLButtonElement | null>(null);
    const { t } = usePaldockTranslation("components.connection-creator");

    useEffect(() => {
        if (open) firstActionRef.current?.focus();
    }, [open]);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ nodeKey: string }>).detail;
            if (detail.nodeKey !== nodeKey) {
                setOpen(false);
            }
        };
        document.addEventListener("tag-popover-open", handler);
        return () => document.removeEventListener("tag-popover-open", handler);
    }, [nodeKey]);

    const handleEdit = () => {
        setOpen(false);
        editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if (!node) return;
            const textNode = $createTextNode(`{${inner}}`);
            node.replace(textNode);
            // Select just the inner content so the user can immediately retype it.
            textNode.select(1, inner.length + 1);
        });
        window.requestAnimationFrame(() => editor.focus());
    };

    const restoreEditorFocus = () => window.requestAnimationFrame(() => editor.focus());

    const handleRemove = () => {
        setOpen(false);
        editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if (!node) return;
            node.remove();
        });
        restoreEditorFocus();
    };

    const handleCopy = () => {
        setOpen(false);
        const writeText = navigator.clipboard?.writeText?.bind(navigator.clipboard);
        if (!writeText) {
            toast.error(t("tag_input.copy_failed"), { autoClose: 2000 });
            restoreEditorFocus();
            return;
        }
        writeText(`{${inner}}`).then(
            () => {
                toast.info(t("tag_input.copied_to_clipboard"), { autoClose: 2000 });
                restoreEditorFocus();
            },
            () => {
                toast.error(t("tag_input.copy_failed"), { autoClose: 2000 });
                restoreEditorFocus();
            }
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        setOpen((prev) => {
            const next = !prev;
            if (next) {
                document.dispatchEvent(new CustomEvent("tag-popover-open", { detail: { nodeKey } }));
            }
            return next;
        });
    };

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger={[]}
            overlayInnerStyle={{ padding: 0 }}
            content={
                <div className="flex">
                    <button
                        ref={firstActionRef}
                        type="button"
                        className="text-sm p-2.5 hover:bg-gray-100 rounded text-gray-700"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={handleEdit}
                    >
                        {t("tag_input.edit")}
                    </button>
                    <button
                        type="button"
                        className="text-sm p-2.5 hover:bg-gray-100 rounded text-gray-700"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={handleCopy}
                    >
                        {t("tag_input.copy")}
                    </button>
                    <button
                        type="button"
                        className="text-sm p-2.5 hover:!bg-red-100 text-red-500 rounded"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={handleRemove}
                    >
                        {t("tag_input.remove")}
                    </button>
                </div>
            }
        >
            <span
                data-tag-node="true"
                data-tag-key={nodeKey}
                data-tag-inner={inner}
                style={{ backgroundColor: color, cursor: "grab" }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-white text-sm mx-0.5 my-0.5 align-middle"
                contentEditable={false}
                tabIndex={0}
                role="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onKeyDown={handleKeyDown}
                onPointerDown={(e) => {
                    if (e.button !== 0) return;

                    // Capture synchronously — React nulls currentTarget after the handler returns.
                    const currentTarget = e.currentTarget as HTMLElement;
                    const startX = e.clientX;
                    const startY = e.clientY;
                    let dragStarted = false;

                    const handlePointerMove = (moveEvent: PointerEvent) => {
                        if (dragStarted) return;
                        const dx = moveEvent.clientX - startX;
                        const dy = moveEvent.clientY - startY;
                        // Start drag only after a small movement threshold.
                        if (Math.hypot(dx, dy) < 4) return;

                        dragStarted = true;
                        const root = currentTarget.closest('[contenteditable="true"]');
                        root?.dispatchEvent(
                            new CustomEvent("tag-drag-start", {
                                detail: { nodeKey, inner, paramType },
                            })
                        );
                    };

                    const cleanup = () => {
                        window.removeEventListener("pointermove", handlePointerMove);
                        window.removeEventListener("pointerup", handlePointerUp);
                        window.removeEventListener("pointercancel", cleanup);
                    };

                    const handlePointerUp = () => {
                        cleanup();
                        if (!dragStarted) {
                            setOpen((prev) => {
                                const next = !prev;
                                if (next) {
                                    document.dispatchEvent(
                                        new CustomEvent("tag-popover-open", { detail: { nodeKey } })
                                    );
                                }
                                return next;
                            });
                        }
                    };

                    window.addEventListener("pointermove", handlePointerMove);
                    window.addEventListener("pointerup", handlePointerUp);
                    window.addEventListener("pointercancel", cleanup);
                }}
            >
                {NodeIcon && <NodeIcon className="w-2.5 h-2.5 flex-shrink-0" />}
                {displayLabel}
            </span>
        </Popover>
    );
};

// ============================================================================
// TagNode
// ============================================================================

export class TagNode extends DecoratorNode<ReactNode> {
    __inner: string;
    __paramType: TagParamType;
    __valid: boolean;
    __displayLabel: string | undefined;
    __nodeType: string | undefined;

    static getType(): string {
        return "tag";
    }

    static clone(node: TagNode): TagNode {
        return new TagNode(
            node.__inner,
            node.__paramType,
            node.__valid,
            node.__displayLabel,
            node.__key,
            node.__nodeType
        );
    }

    static importJSON(serializedNode: SerializedTagNode): TagNode {
        return new TagNode(
            serializedNode.inner,
            serializedNode.paramType,
            serializedNode.valid ?? true,
            serializedNode.displayLabel,
            undefined,
            serializedNode.nodeType
        );
    }

    constructor(
        inner: string,
        paramType?: TagParamType,
        valid = true,
        displayLabel?: string,
        key?: NodeKey,
        nodeType?: string
    ) {
        super(key);
        this.__inner = inner;
        this.__paramType = paramType ?? inferParamType(inner);
        this.__valid = valid;
        this.__displayLabel = displayLabel;
        this.__nodeType = nodeType;
    }

    exportJSON(): SerializedTagNode {
        return {
            ...super.exportJSON(),
            inner: this.__inner,
            paramType: this.__paramType,
            valid: this.__valid,
            displayLabel: this.__displayLabel,
            nodeType: this.__nodeType,
            type: "tag",
            version: 1,
        };
    }

    getInner(): string {
        return this.getLatest().__inner;
    }

    getParamType(): TagParamType {
        return this.getLatest().__paramType;
    }

    getNodeType(): string | undefined {
        return this.getLatest().__nodeType;
    }

    isValid(): boolean {
        return this.getLatest().__valid;
    }

    getDisplayLabel(): string {
        return this.getLatest().__displayLabel ?? this.getLatest().__inner;
    }

    setValidation(valid: boolean, paramType?: TagParamType): void {
        const writable = this.getWritable();
        writable.__valid = valid;
        if (paramType !== undefined) {
            writable.__paramType = paramType;
        }
    }

    setDisplayLabel(displayLabel: string | undefined): void {
        const writable = this.getWritable();
        writable.__displayLabel = displayLabel;
    }

    setNodeType(nodeType: string | undefined): void {
        const writable = this.getWritable();
        writable.__nodeType = nodeType;
    }

    createDOM(): HTMLElement {
        const span = document.createElement("span");
        span.style.display = "inline-flex";
        span.style.alignItems = "center";
        span.style.verticalAlign = "middle";
        return span;
    }

    updateDOM(): false {
        return false;
    }

    isInline(): boolean {
        return true;
    }

    isKeyboardSelectable(): boolean {
        // false makes ArrowLeft/ArrowRight step straight past this node in a single press instead
        // of stopping to select it first (Lexical's default for keyboard-selectable decorators).
        // Backspace/Delete still remove the node when the caret lands adjacent to it — that's
        // Lexical's default $deleteCharacter handling for decorator boundaries, independent of
        // keyboard-selectability — verified against a real Lexical instance.
        return false;
    }

    decorate(): ReactNode {
        const latest = this.getLatest();
        const inner = latest.__inner;
        // Show the raw value incl. context prefixes (parsedBody./query./headers.); only the
        // form-field `data_` prefix stays hidden.
        const displayLabel =
            latest.__displayLabel ??
            (latest.__valid &&
            ["form_fields", "global_fields", "local_fields"].includes(latest.__paramType ?? "") &&
            inner.startsWith("data_")
                ? inner.slice(5)
                : inner);
        const color = latest.__valid ? getPillColor(latest.__paramType) : INVALID_PILL_COLOR;

        return (
            <TagNodeView
                color={color}
                inner={inner}
                displayLabel={displayLabel}
                nodeKey={latest.__key}
                paramType={latest.__paramType}
                nodeType={latest.__nodeType}
            />
        );
    }
}

// ============================================================================
// Factories
// ============================================================================

export function $createTagNode(
    inner: string,
    paramType?: TagParamType,
    valid = true,
    displayLabel?: string,
    nodeType?: string
): TagNode {
    return $applyNodeReplacement(new TagNode(inner, paramType, valid, displayLabel, undefined, nodeType));
}

export function $isTagNode(node: LexicalNode | null | undefined): node is TagNode {
    return node instanceof TagNode;
}
